import { Link } from "react-router-dom";

import { Badge, Field, Fields, Panel, Surface, Table, Td, Th } from "../../components/ui";
import type { AppConfig } from "../../config";
import { useResource } from "../../hooks/useResource";
import { countsFor, coverageFor } from "../../products/coverage";
import { PRODUCTS, type ProductDef } from "../../products/registry";

/**
 * Products & health — what this console covers, per product, and what it does
 * not.
 *
 * TWO QUESTIONS, and they need different machinery:
 *
 *   Is the product reachable right now?   A live probe of /admin/health, which
 *                                         is the one unauthenticated route. If
 *                                         every other page 401s while this is
 *                                         green, the fault is the token rather
 *                                         than the gateway.
 *
 *   Does the console cover it at all?     A statement, from products/coverage.
 *                                         No request can discover whether a
 *                                         Labeeb envelope has been built.
 *
 * The second is the one people actually come here for. A surface drawn blurred
 * elsewhere in the console says what IT needs; this is the only place the whole
 * list is in one column, which is what makes "what is left before Labeeb ships"
 * answerable without opening every tab.
 */
export function Health({ config, embedded = false }: { config: AppConfig; embedded?: boolean }) {
  return (
    <Surface embedded={embedded} title="Products & health" subtitle={`Gateway: ${config.apiUrl}`}>
      <div className="space-y-6">
        {PRODUCTS.map((p) => (
          <ProductBlock key={p.id} product={p} config={config} />
        ))}
      </div>
    </Surface>
  );
}

function ProductBlock({ product, config }: { product: ProductDef; config: AppConfig }) {
  const counts = countsFor(product.id);
  const rows = coverageFor(product.id);

  return (
    <section>
      <div className="mb-2 flex flex-wrap items-baseline justify-between gap-3">
        <h3 className="text-sm font-semibold text-slate-950 dark:text-white">
          {product.label}{" "}
          <span className="ref ml-1 text-xs font-normal text-slate-500 dark:text-slate-400">
            {product.apiPrefix}/admin/*
          </span>
        </h3>
        <span className="text-xs text-slate-500 dark:text-slate-400">
          {counts.live} live · {counts.absent} not built
        </span>
      </div>

      <Panel>
        <Fields>
          <Field label="What it is">{product.blurb}</Field>
          <Field label="Routes on this platform">
            {product.declared ? (
              <ProbeStatus product={product} config={config} />
            ) : (
              <>
                <Badge tone="amber">None declared</Badge>
                {/* The distinction that matters: this is not an outage and no
                    deploy fixes it. `products.py` maps a product name to the
                    routes the gateway mounts, and there is no entry — so there
                    is nothing to call in ANY environment. */}
                <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
                  <span className="ref">infrastructure/infra/products.py</span> has no{" "}
                  <span className="ref">{product.id}</span> entry, so the gateway mounts no route
                  for it in any environment. This is not an outage — nothing is deployed to be
                  down.
                </p>
                {product.needs && (
                  <p className="mt-1.5 text-xs text-slate-500 dark:text-slate-400">
                    <strong>To wire it:</strong> {product.needs}. Both changes are the platform
                    team&rsquo;s and the product team&rsquo;s — none of them are in this console.
                  </p>
                )}
              </>
            )}
          </Field>
        </Fields>
      </Panel>

      <div className="mt-3">
        <Table
          head={
            <tr>
              <Th>Surface</Th>
              <Th>State</Th>
              <Th>Behind it</Th>
            </tr>
          }
        >
          {rows.map((r) => (
            <tr key={`${r.product}-${r.surface}`}>
              <Td>
                <Link to={r.to} className="text-brand-600 hover:underline dark:text-brand-300">
                  {r.surface}
                </Link>
              </Td>
              <Td>
                {r.status === "live" ? (
                  <Badge tone="green">Live</Badge>
                ) : (
                  <Badge tone="neutral">Not built</Badge>
                )}
              </Td>
              <Td>
                <span className="text-xs text-slate-500 dark:text-slate-400">{r.by}</span>
              </Td>
            </tr>
          ))}
        </Table>
      </div>
    </section>
  );
}

/**
 * A declared product can still be unmounted HERE — MCP in prod is exactly
 * that, because its `lambda_arn` parameter has never been published. The probe
 * is the only thing that separates that from an outage, and the two call for
 * different actions, so they get different words.
 */
function ProbeStatus({ product, config }: { product: ProductDef; config: AppConfig }) {
  const { state } = useResource<{ ok: boolean }>(config, `${product.apiPrefix}/admin/health`);

  if (state.kind === "loading") return <Badge tone="neutral">Checking…</Badge>;

  if (state.kind === "ready") {
    return (
      <>
        <Badge tone="green">Live</Badge>
        <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">Health returned ok.</p>
      </>
    );
  }

  if (state.kind === "notfound") {
    return (
      <>
        <Badge tone="amber">Not mounted here</Badge>
        <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
          Declared, but this gateway mounted no route for it — its{" "}
          <span className="ref">lambda_arn</span> SSM parameter is absent in this environment.
          Expected for a product whose stack has not deployed here yet.
        </p>
      </>
    );
  }

  /*
   * The remaining states are named rather than swept into "Unreachable",
   * because on THIS route each one means something the others do not. Health
   * is the single unauthenticated route on the gateway, so a refusal here is
   * not an outage — it is a statement about the request, and an operator told
   * "unreachable" would go looking for a downed Lambda.
   */
  if (state.kind === "signedout") {
    return (
      <>
        <Badge tone="neutral">Not checked</Badge>
        <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
          You are signed out, so the probe did not run. It says nothing about the product.
        </p>
      </>
    );
  }

  if (state.kind === "denied") {
    return (
      <>
        <Badge tone="amber">Refused</Badge>
        <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
          Health is the one route that needs no token, so a refusal here is the gateway&rsquo;s,
          not the product&rsquo;s — the product may be perfectly healthy behind it.
        </p>
      </>
    );
  }

  if (state.kind === "invalid") {
    return (
      <>
        <Badge tone="amber">Answered, but not with liveness</Badge>
        <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
          Something is serving this path and it is not the product&rsquo;s health route.
        </p>
      </>
    );
  }

  if (state.kind === "unavailable") {
    return (
      <>
        <Badge tone="red">Down</Badge>
        <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
          The gateway reached the product and it did not answer. Every figure this console shows
          from it is stale. {state.error.message}
        </p>
      </>
    );
  }

  if (state.kind === "error") {
    return (
      <>
        <Badge tone="red">Unreachable</Badge>
        <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
          The request itself failed, so this may be the network between you and the gateway
          rather than the product. {state.error.message}
        </p>
      </>
    );
  }

  // Every variant of Resource is named above. Left rather than removed so a
  // new variant lands here visibly instead of rendering nothing.
  return <Badge tone="neutral">Unknown</Badge>;
}
